import * as React from 'react';
import { debounce } from 'debounce';
import { Form, Dropdown, InputProps, DropdownProps, ButtonProps, RadioProps, Pagination, PaginationProps } from 'semantic-ui-react';
import { countryOptions, yearOptions, dayOptions, monthOptions } from '../dropdownOptions';
import { Beneficiary as BeneficiaryModel } from '../../../models/applicationForm';

interface BeneficiaryInputState {
  currentStep: number,
  surname?: string,
  name?: string,
  dateOfBirthYear?: number | null,
  dateOfBirthMonth?: string,
  dateOfBirthDay?: number | null,
  countryOfCitizenship?: string,
  country?: string,
  relationshipToHolder?: string,
  percentageBenefit?: string,
  hasGuardian?: boolean,
  guardianName?: string,
  guardianSurname?: string,
  guardianDateOfBirthDay?: number | null,
  guardianDateOfBirthMonth?: string,
  guardianDateOfBirthYear?: number | null,
  guardianAddress?: string,
  guardianCountry?: string,
  guardianCountryOfCitizenship?: string
}

interface BeneficiaryInputProps {
  beneficiary: BeneficiaryModel,
  setBeneficiary: (beneficiary: BeneficiaryModel) => object
}

class Beneficiary extends React.Component<BeneficiaryInputProps,BeneficiaryInputState> {
  public constructor(props: BeneficiaryInputProps) {
    super(props);

    const dateOfBirthAsDate: Date | null = this.props.beneficiary.dateOfBirth ?
      new Date(this.props.beneficiary.dateOfBirth) : null;

    const guardianDateOfBirthAsDate: Date | null = this.props.beneficiary.guardianDateOfBirth ?
      new Date(this.props.beneficiary.guardianDateOfBirth) : null;

    this.state = {
      currentStep: 1,
      surname: this.props.beneficiary.surname,
      name: this.props.beneficiary.name,
      dateOfBirthYear: dateOfBirthAsDate ?
        dateOfBirthAsDate.getFullYear() : null,
      dateOfBirthMonth: dateOfBirthAsDate ?
        monthOptions[dateOfBirthAsDate.getMonth()].text : '',
      dateOfBirthDay: dateOfBirthAsDate ?
        dateOfBirthAsDate.getDate() : null,
      countryOfCitizenship: this.props.beneficiary.countryOfCitizenship,
      country: this.props.beneficiary.country,
      relationshipToHolder: this.props.beneficiary.relationshipToHolder,
      percentageBenefit: this.props.beneficiary.percentageBenefit ?
        this.props.beneficiary.percentageBenefit.toString() : '',
      hasGuardian: this.props.beneficiary.hasGuardian,
      guardianName: this.props.beneficiary.guardianName,
      guardianSurname: this.props.beneficiary.guardianSurname,
      guardianDateOfBirthYear: guardianDateOfBirthAsDate ?
        guardianDateOfBirthAsDate.getFullYear() : null,
      guardianDateOfBirthMonth: guardianDateOfBirthAsDate ?
        monthOptions[guardianDateOfBirthAsDate.getMonth()].text : '',
      guardianDateOfBirthDay: guardianDateOfBirthAsDate ?
       guardianDateOfBirthAsDate.getDate() : null,
      guardianAddress: this.props.beneficiary.guardianAddress,
      guardianCountry: this.props.beneficiary.guardianCountry,
      guardianCountryOfCitizenship: this.props.beneficiary.guardianCountryOfCitizenship
    };

    this.setBeneficiary = debounce(this.setBeneficiary, 500);
  }

  public render() {
    return (
      <div className='application-form-wrap'>
        <h6>Section 3 - Step {this.state.currentStep}</h6>
        <h1>BENEFICIARY DESIGNATION</h1>
        <Form>
        {/* Step 1 Information */ }
        { this.state.currentStep === 1 &&
        <div className='card'>
          <div>
            <ul className='ui list'>
              <li>A maximum of ten beneficiaries can be designated at any one time. If more than five beneficiaries are required, then attach a further copy of this page. Any further change to beneficiaries must be signed by all Plan holders and submitted to Sanlam Global.</li>
              <li>The Primary Beneficiaries designated will receive the value of the Plan, less any applicable fees and charges, in equal shares, unless otherwise stated, on the death of the last surviving Plan holder. If a Primary Beneficiary dies before the last surviving Plan holder, that person's share will be divided equally among the surviving Primary Beneficiaries (if any), unless otherwise specified.</li>
              <li>If none of the Primary Beneficiaries survive the last surviving Plan holder, any designated Contingent Beneficiaries will be entitled to the payment.</li>
              <li>Documentary evidence of identification and physical residential address of the person(s) designated will be required before a payment can be made.</li>
              <li>Depending on the country of residence or nationality of the beneficiary, it may not be possible for a designated beneficiary to receive proceeds directly.</li>
            </ul>
            <Form.Button onClick={this.next}>Next</Form.Button>
          </div>
        </div>
        }
        {/* Step 2 Beneficiary Details */ }
        { this.state.currentStep === 2 &&
        <div className='card'>        
          <Form.Group widths='equal'>
            <Form.Input
              name='surname'
              fluid={true}
              label='Surname'
              placeholder='Surname'
              value={this.state.surname}
              onChange={this.handleInputChange}
            />
            <Form.Input
              name='name'
              fluid={true}
              label='Full first and middle names'
              placeholder='Full first and middle names'
              value={this.state.name}
              onChange={this.handleInputChange}
            />
            <Form.Input fluid={true} label='Date of birth'>
              <Form.Select
                name='dateOfBirthDay'
                compact={true}
                options={dayOptions}
                value={this.state.dateOfBirthDay ? this.state.dateOfBirthDay.toString() : ''}
                onChange={this.handleDropdownChange}
              />
              <Form.Select
                name='dateOfBirthMonth'
                compact={true}
                options={monthOptions}
                value={this.state.dateOfBirthMonth}
                onChange={this.handleDropdownChange}
              />
              <Form.Select
                name='dateOfBirthYear'
                compact={true}
                options={yearOptions}
                value={this.state.dateOfBirthYear ? this.state.dateOfBirthYear.toString() : ''}
                onChange={this.handleDropdownChange}
              />
            </Form.Input>
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Input fluid={true} label='Country of citizenship'>
              <Dropdown
                placeholder='Select Country'
                fluid={true}
                search={true}
                selection={true}
                options={countryOptions}
                name='countryOfCitizenship'
                value={this.state.countryOfCitizenship}
                onChange={this.handleDropdownChange}
              />
            </Form.Input>
            <Form.Input fluid={true} label='Country of residence'>
              <Dropdown
                placeholder='Select Country'
                fluid={true}
                search={true}
                selection={true}
                options={countryOptions}
                name='country'
                value={this.state.country}
                onChange={this.handleDropdownChange}
              />
            </Form.Input>
            <Form.Input
              name='relationshipToHolder'
              fluid={true}
              label='Relationshop to planholder'
              placeholder='Relationshop to planholder'
              value={this.state.relationshipToHolder}
              onChange={this.handleInputChange}
            />
          </Form.Group>
          <Form.Group>
            <Form.Radio
              toggle={true}
              name='hasGuardian'
              label='Assign guardian'
              onChange={this.handleRadioToggleChange}
              checked={this.state.hasGuardian}
            />
          </Form.Group>
          {
            this.state.hasGuardian &&

            <div>
            <h3>Guardian Designation</h3>
          <Form.Group widths='equal'>
            <Form.Input
              name='guardianSurname'
              fluid={true}
              label='Guardian Surname'
              placeholder='Surname'
              value={this.state.guardianSurname}
              onChange={this.handleInputChange}
            />
            <Form.Input
              name='guardianName'
              fluid={true}
              label='Full first and middle names of Guardian'
              placeholder='Full first and middle names of Guardian'
              value={this.state.guardianName}
              onChange={this.handleInputChange}
            />
            <Form.Input fluid={true} label='Guardian Date of birth'>
              <Form.Select
                name='guardianDateOfBirthDay'
                compact={true}
                options={dayOptions}
                value={this.state.guardianDateOfBirthDay ? this.state.guardianDateOfBirthDay.toString() : ''}
                onChange={this.handleDropdownChange}
              />
              <Form.Select
                name='guardianDateOfBirthMonth'
                compact={true}
                options={monthOptions}
                value={this.state.guardianDateOfBirthMonth}
                onChange={this.handleDropdownChange}
              />
              <Form.Select
                name='guardianDateOfBirthYear'
                compact={true}
                options={yearOptions}
                value={this.state.guardianDateOfBirthYear ? this.state.guardianDateOfBirthYear.toString() : ''}
                onChange={this.handleDropdownChange}
              />
            </Form.Input>
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Input
              name='guardianAddress'
              label='Guardian Street address'
              placeholder='Guardian Street address'
              value={this.state.guardianAddress}
              onChange={this.handleInputChange}
            />
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Input fluid={true} label='Guardian Country of residence'>
              <Dropdown
                placeholder='Select Country'
                fluid={true}
                search={true}
                selection={true}
                options={countryOptions}
                name='guardianCountry'
                value={this.state.guardianCountry}
                onChange={this.handleDropdownChange}
              />
            </Form.Input>
            <Form.Input fluid={true} label='Guardian Country of citizenship'>
              <Dropdown
                placeholder='Select Country'
                fluid={true}
                search={true}
                selection={true}
                options={countryOptions}
                name='guardianCountryOfCitizenship'
                value={this.state.guardianCountryOfCitizenship}
                onChange={this.handleDropdownChange}
              />
            </Form.Input>
          </Form.Group>
          </div>
          }
          <Form.Group>
            <Form.Input
              name='percentageBenefit'
              label='% benefit'
              placeholder='% benefit'
              value={this.state.percentageBenefit}
              onChange={this.handleInputChange}
            />
          </Form.Group>
          <Form.Button onClick={this.handleSubmit}>Submit</Form.Button>
        </div>
        }
        <Pagination
          className='application-form-pagination'
          activePage={this.state.currentStep}
          onPageChange={this.handlePaginationChange}
          totalPages={2}
          firstItem={null}
          lastItem={null}
          prevItem={null}
          nextItem={null}
        />
        </Form>
      </div>
    )
  }

  private handleInputChange = (event: React.SyntheticEvent<HTMLElement, Event>, input: InputProps) => {
    this.handleChange(input.name, input.value);
  }

  private handleDropdownChange = (event: React.SyntheticEvent<HTMLElement, Event>, dropDown: DropdownProps) => {
    this.handleChange(dropDown.name, dropDown.value);
  }

  private handleRadioToggleChange = (event: React.FormEvent<HTMLElement>, radioToggle: RadioProps) => {
    this.handleChange(radioToggle.name || 'undefined', radioToggle.checked);
  }

  private handlePaginationChange = (event: React.MouseEvent<HTMLAnchorElement>, pagination: PaginationProps) => {
    this.handleChange('currentStep', pagination.activePage);
   }

  private next = () => this.setState({ currentStep: this.state.currentStep + 1});

  private handleChange = (key: string, value: any) => {
    this.setState({
      [key]: value
    } as BeneficiaryInputState, () => {
      this.setBeneficiary();
    });
  }

  private handleSubmit = (event: React.FormEvent<HTMLButtonElement>, button: ButtonProps) => {
    event.preventDefault();

    // TODO => Submit info
  }

  private setBeneficiary = () => {
    this.props.setBeneficiary(this.mapStateToBeneficiary());
  }

  private mapStateToBeneficiary = () : BeneficiaryModel => ({
    surname: this.state.surname || '',
    name: this.state.name || '',
    dateOfBirth: parseInt(new Date(`${this.state.dateOfBirthDay}-${this.state.dateOfBirthMonth}-${this.state.dateOfBirthYear}`)
      .getTime().toFixed(0), 10),
    countryOfCitizenship: this.state.countryOfCitizenship || '',
    country: this.state.country || '',
    relationshipToHolder: this.state.relationshipToHolder || '',
    hasGuardian: this.state.hasGuardian || false,
    guardianSurname: this.state.guardianSurname || '',
    guardianName: this.state.guardianName || '',
    guardianDateOfBirth: parseInt(new Date(`${this.state.guardianDateOfBirthDay}-${this.state.guardianDateOfBirthMonth}-${this.state.guardianDateOfBirthYear}`)
      .getTime().toFixed(0), 10),
    guardianAddress: this.state.guardianAddress || '',
    guardianCountry: this.state.guardianCountry || '',
    guardianCountryOfCitizenship: this.state.guardianCountryOfCitizenship || '',
    percentageBenefit: (this.state.percentageBenefit && this.state.percentageBenefit !== '') ?
      parseInt(this.state.percentageBenefit, 10) : null
  });
}

export default Beneficiary;